import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { SequenceResultsClient } from "@clients/firestore/sequence-results.client";
import { REGULAR_SEQUENCE_LENGTH } from "@config";
import { Answer } from "@domain/Answer";
import { Exercise } from "@domain/Exercise";
import { CurrentSequence, Sequence } from "@domain/Sequence";
import { AnalyticsService } from "@services/analytics.service";
import { AuthState } from "@states/auth.state";

@Injectable({
  providedIn: "root",
})
export class SequenceService {
  private sequenceResultsClient = inject(SequenceResultsClient);
  private analyticsService = inject(AnalyticsService);
  private authState = inject(AuthState);

  REGULAR_SEQUENCE_LENGTH = REGULAR_SEQUENCE_LENGTH;
  currentSequenceLength = REGULAR_SEQUENCE_LENGTH;
  currentExerciseNumber = 0;
  isInitialized = false;
  numberOfCorrectAnswers = 0;
  numberOfIncorrectAnswers = 0;

  sequence: WritableSignal<Sequence>;
  exercisesToRework = signal<Exercise[]>([]);
  isCurrentSequenceAborted = signal(false);
  isCurrentSequenceCompleted = signal(false);
  isInReworkErrorsMode = signal(false);
  reworkSessionCount = signal(0);

  constructor() {
    this.sequence = signal(new Sequence([]));
  }

  reset(): void {
    this.currentSequenceLength = this.REGULAR_SEQUENCE_LENGTH;
    this.currentExerciseNumber = 0;
    this.isInitialized = false;
    this.numberOfCorrectAnswers = 0;
    this.numberOfIncorrectAnswers = 0;

    this.isInReworkErrorsMode.set(false);
    this.reworkSessionCount.set(0);
    this.isCurrentSequenceAborted.set(false);
    this.isCurrentSequenceCompleted.set(false);
    this.exercisesToRework.set([]);
    window.localStorage.removeItem("results");
    this.clearResults();
  }

  initializeSequence(): void {
    this.currentExerciseNumber = 1;
    this.isInitialized = true;
    this.numberOfCorrectAnswers = 0;
    this.numberOfIncorrectAnswers = 0;
    this.isCurrentSequenceAborted.set(false);
    this.isCurrentSequenceCompleted.set(false);
  }

  clearResults() {
    this.sequence.set(new Sequence([]));
  }

  hasAtLeastOneAnswer(): boolean {
    return this.sequence().numberOfResults() > 0;
  }

  hasIncorrectAnswers(): boolean {
    for (const result of this.sequence().getResults()) {
      if (result.answer.isIncorrect()) return true;
    }

    return false;
  }

  goToReworkErrorsMode(): void {
    this.currentExerciseNumber = 0;
    this.currentSequenceLength = this.sequence().getIncorrectResults().length;
    this.isInitialized = false;

    this.isInReworkErrorsMode.set(false);
    this.isInReworkErrorsMode.set(true);
    this.reworkSessionCount.update(n => n + 1);
    this.exercisesToRework.set(
      this.sequence()
        .getIncorrectResults()
        .map(({ exercise }) => exercise)
    );
  }

  goToNextExercise(): void {
    if (this.currentExerciseNumber < this.currentSequenceLength) {
      this.currentExerciseNumber += 1;
    }
  }

  isLastExerciseInSequence(): boolean {
    return this.currentExerciseNumber === this.currentSequenceLength;
  }

  abortSequence() {
    this.isCurrentSequenceAborted.set(true);
  }

  completeSequence() {
    this.isCurrentSequenceCompleted.set(true);
  }

  updateSequence(exercise: Exercise, answer: Answer): void {
    if (this.isInReworkErrorsMode()) {
      this.exercisesToRework.update(exerciseToRework => exerciseToRework.filter(({ id }: Exercise) => !id.isEqualTo(exercise.id)));
      this.sequence.update(sequence => sequence.updateResult(exercise, answer));
    } else {
      this.sequence.update(sequence => sequence.addResult(exercise, answer));
    }

    this.saveSequenceToDatabase();
    this.incrementAnswerCount(answer);
  }

  private async saveSequenceToDatabase(): Promise<void> {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return;

    const sequence = this.sequence();
    try {
      if (sequence instanceof CurrentSequence) {
        await this.sequenceResultsClient.updateSequence(sequence);
      } else {
        const currentSequenceId = await this.sequenceResultsClient.createSequence(currentUser, sequence);
        this.sequence.update(sequence => new CurrentSequence(currentSequenceId, sequence.getResults()));
      }
    } catch (error) {
      if (error instanceof Error) {
        this.analyticsService.trackException(error);
      }
    }
  }

  private incrementAnswerCount(answer: Answer) {
    if (answer.isCorrect()) {
      this.numberOfCorrectAnswers += 1;
    } else {
      this.numberOfIncorrectAnswers += 1;
    }
  }
}
